// Exportación integral del empleado a Excel o PDF.
// Incluye: Información Personal, Laboral y Salarial.
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { colones, fmtFecha } from "@/lib/api";

const sanitize = (s) => String(s || "").replace(/[^\w\s.-]/g, "_").replace(/\s+/g, "_").trim();

// ----- Helpers -----
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmtDate = (v) => (v ? fmtFecha(v) : "—");
const fmtBool = (v) => (v ? "Sí" : "No");
const fmtMoney = (v) => colones(Number(v) || 0);

function buildSections(e, empresa = {}) {
  const salario = Number(e.salario_base) || 0;
  const grupoAct = empresa?.actividad_grupo;
  const freq = String(e.frecuencia_pago || "Mensual").toLowerCase();
  const diasBase =
    grupoAct === "servicios_industria" && freq === "semanal" ? 26 : 30;
  const diario = salario / diasBase;
  const horaDiurna = diario / 8;
  const horaMixta = diario / 7;
  const horaNocturna = diario / 6;

  const personal = [
    ["Nombre completo", `${val(e.nombre)} ${val(e.apellidos)}`.trim()],
    ["Cédula", val(e.cedula)],
    ["Fecha de nacimiento", fmtDate(e.fecha_nacimiento)],
    ["Género", val(e.genero)],
    ["Estado civil", val(e.estado_civil)],
    ["Teléfono", val(e.telefono)],
    ["Correo electrónico", val(e.correo)],
    ["Dirección", val(e.direccion)],
    ["Contacto de emergencia", val(e.contacto_emergencia)],
    ["Parentesco", val(e.parentesco_emergencia)],
    ["Teléfono de emergencia", val(e.telefono_emergencia)],
  ];

  const laboral = [
    ["Código de empleado", val(e.codigo)],
    ["Estado", val(e.estado)],
    ["Departamento", val(e.departamento)],
    ["Puesto", val(e.puesto)],
    ["Fecha de ingreso", fmtDate(e.fecha_ingreso)],
    ["Jornada", val(e.jornada)],
    ["Frecuencia de pago", val(e.frecuencia_pago)],
    ["N.º Seguro Social", val(e.numero_seguro_social)],
    ["Banco", val(e.banco)],
    ["Cuenta bancaria", val(e.cuenta_bancaria)],
    ["Vacaciones disponibles", e.__vacaciones_saldo != null ? `${e.__vacaciones_saldo} días` : "—"],
    ["CCSS aplica", fmtBool(e.ccss_aplica)],
    ["Pensionado", fmtBool(e.pensionado)],
    ["Fecha ingreso CCSS", fmtDate(e.fecha_ccss)],
    ["Hora de almuerzo paga", fmtBool(e.almuerzo_paga)],
    ["Reporta a medio tiempo", fmtBool(e.reportar_medio_tiempo)],
    ["Notas", val(e.notas)],
  ];

  const salarial = [
    ["Salario base", fmtMoney(salario)],
    ["Salario mensual", fmtMoney(salario)],
    ["Salario quincenal", fmtMoney(salario / 2)],
    ["Salario semanal", fmtMoney(salario / 4.33)],
    ["Salario diario (base ÷ " + diasBase + ")", fmtMoney(diario)],
    ["Hora Diurna (8h)", fmtMoney(horaDiurna)],
    ["Hora Mixta (7h)", fmtMoney(horaMixta)],
    ["Hora Nocturna (6h)", fmtMoney(horaNocturna)],
    ["Hora Diurna Extra (×1.5)", fmtMoney(horaDiurna * 1.5)],
    ["Hora Mixta Extra (×1.5)", fmtMoney(horaMixta * 1.5)],
    ["Hora Nocturna Extra (×1.5)", fmtMoney(horaNocturna * 1.5)],
  ];

  // Historial de aumentos (si existe)
  const historial = Array.isArray(e.salario_historial) ? e.salario_historial : [];

  return { personal, laboral, salarial, historial };
}

function filename(e, ext) {
  const nombre = `${e.nombre || ""} ${e.apellidos || ""}`.trim();
  const safe = sanitize(nombre.normalize("NFD").replace(/[\u0300-\u036f]/g, "")) || "empleado";
  const stamp = new Date().toISOString().slice(0, 10);
  return `Ficha_${safe}_${stamp}.${ext}`;
}

// ============================================================
// Excel export
// ============================================================
export function exportarEmpleadoExcel(empleado, empresa = {}) {
  const e = empleado;
  const { personal, laboral, salarial, historial } = buildSections(e, empresa);

  const wb = XLSX.utils.book_new();

  const sectionHeader = (title) => [
    { v: title, s: {
      font: { bold: true, sz: 13, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "274735" } },
      alignment: { horizontal: "left", vertical: "center" },
    } },
    { v: "", s: { fill: { fgColor: { rgb: "274735" } } } },
  ];
  const rowStyle = (isEven) => ({
    font: { sz: 11, color: { rgb: "1E1E1E" } },
    fill: { fgColor: { rgb: isEven ? "FAFAF7" : "FFFFFF" } },
    alignment: { vertical: "center", wrapText: true },
  });
  const labelStyle = { font: { bold: true, sz: 11 }, alignment: { vertical: "center" } };

  const buildSheet = (rows, title) => {
    const aoa = [];
    aoa.push([{ v: empresa?.razon_social || empresa?.nombre || "", s: { font: { italic: true, sz: 10, color: { rgb: "555555" } } } }]);
    aoa.push([{ v: `${e.nombre || ""} ${e.apellidos || ""}`.trim(), s: { font: { bold: true, sz: 15, color: { rgb: "274735" } } } }]);
    aoa.push([{ v: `Cédula: ${e.cedula || "—"}   ·   Generado: ${fmtFecha(new Date().toISOString().slice(0, 10))}`, s: { font: { sz: 10, color: { rgb: "666666" } } } }]);
    aoa.push([""]);
    aoa.push(sectionHeader(title));
    rows.forEach((r, i) => {
      aoa.push([
        { v: r[0], s: { ...labelStyle, ...rowStyle(i % 2 === 0) } },
        { v: r[1], s: rowStyle(i % 2 === 0) },
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 32 }, { wch: 48 }];
    // merges para header/title
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } },
      { s: { r: 4, c: 0 }, e: { r: 4, c: 1 } },
    ];
    return ws;
  };

  XLSX.utils.book_append_sheet(wb, buildSheet(personal, "Información Personal"), "Personal");
  XLSX.utils.book_append_sheet(wb, buildSheet(laboral, "Información Laboral"), "Laboral");
  XLSX.utils.book_append_sheet(wb, buildSheet(salarial, "Información Salarial"), "Salarial");

  // Hoja opcional: Historial de aumentos
  if (historial.length > 0) {
    const aoa = [
      [{ v: "Historial de Aumentos de Salario", s: {
        font: { bold: true, sz: 13, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "274735" } },
      } }, "", "", ""],
      [
        { v: "Fecha", s: { font: { bold: true }, fill: { fgColor: { rgb: "E6EFEA" } } } },
        { v: "Salario Anterior", s: { font: { bold: true }, fill: { fgColor: { rgb: "E6EFEA" } } } },
        { v: "Salario Nuevo", s: { font: { bold: true }, fill: { fgColor: { rgb: "E6EFEA" } } } },
        { v: "Incremento", s: { font: { bold: true }, fill: { fgColor: { rgb: "E6EFEA" } } } },
      ],
      ...historial.map((h) => [
        fmtDate(h.fecha_desde),
        fmtMoney(h.salario_anterior),
        fmtMoney(h.salario_nuevo),
        fmtMoney((h.salario_nuevo || 0) - (h.salario_anterior || 0)),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 18 }, { wch: 22 }, { wch: 22 }, { wch: 22 }];
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
    XLSX.utils.book_append_sheet(wb, ws, "Historial Salarial");
  }

  XLSX.writeFile(wb, filename(e, "xlsx"));
}

// ============================================================
// PDF export
// ============================================================
export function exportarEmpleadoPDF(empleado, empresa = {}) {
  const e = empleado;
  const { personal, laboral, salarial, historial } = buildSections(e, empresa);

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;

  // Header
  doc.setFillColor(39, 71, 53); // #274735
  doc.rect(0, 0, pageWidth, 80, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(`${e.nombre || ""} ${e.apellidos || ""}`.trim() || "Empleado", marginX, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Cédula: ${e.cedula || "—"}   ·   ${e.puesto || "—"}${e.departamento ? " · " + e.departamento : ""}`, marginX, 52);
  doc.setFontSize(9);
  doc.setTextColor(220, 230, 224);
  doc.text(`${empresa?.razon_social || empresa?.nombre || ""}   ·   Generado: ${fmtFecha(new Date().toISOString().slice(0, 10))}`, marginX, 68);

  const drawSection = (startY, title, rows) => {
    autoTable(doc, {
      startY,
      head: [[{ content: title, colSpan: 2, styles: { fillColor: [39, 71, 53], textColor: 255, fontStyle: "bold", fontSize: 11, halign: "left" } }]],
      body: rows.map((r) => [
        { content: r[0], styles: { fontStyle: "bold", cellWidth: 180, fillColor: [250, 250, 247] } },
        { content: r[1], styles: { cellWidth: "auto" } },
      ]),
      theme: "grid",
      styles: { fontSize: 9.5, cellPadding: 6, lineColor: [220, 220, 210], lineWidth: 0.4, textColor: [30, 30, 30] },
      margin: { left: marginX, right: marginX },
    });
    return doc.lastAutoTable.finalY + 12;
  };

  let y = 100;
  y = drawSection(y, "Información Personal", personal);
  y = drawSection(y, "Información Laboral", laboral);

  // Sección Salarial (nueva página si no cabe)
  if (y > 620) { doc.addPage(); y = 40; }
  y = drawSection(y, "Información Salarial", salarial);

  // Historial de aumentos
  if (historial.length > 0) {
    if (y > 620) { doc.addPage(); y = 40; }
    autoTable(doc, {
      startY: y,
      head: [[{ content: "Historial de Aumentos de Salario", colSpan: 4, styles: { fillColor: [39, 71, 53], textColor: 255, fontStyle: "bold", fontSize: 11, halign: "left" } }],
             [{ content: "Fecha", styles: { fillColor: [230, 239, 234], textColor: 30, fontStyle: "bold" } },
              { content: "Anterior", styles: { fillColor: [230, 239, 234], textColor: 30, fontStyle: "bold" } },
              { content: "Nuevo", styles: { fillColor: [230, 239, 234], textColor: 30, fontStyle: "bold" } },
              { content: "Incremento", styles: { fillColor: [230, 239, 234], textColor: 30, fontStyle: "bold" } }]],
      body: historial.map((h) => [
        fmtDate(h.fecha_desde),
        fmtMoney(h.salario_anterior),
        fmtMoney(h.salario_nuevo),
        fmtMoney((h.salario_nuevo || 0) - (h.salario_anterior || 0)),
      ]),
      theme: "grid",
      styles: { fontSize: 9.5, cellPadding: 6, lineColor: [220, 220, 210], lineWidth: 0.4 },
      margin: { left: marginX, right: marginX },
    });
  }

  // Footer con paginación
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - marginX, doc.internal.pageSize.getHeight() - 18, { align: "right" });
  }

  doc.save(filename(e, "pdf"));
}
